import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useActivity } from '../App';

const api = () => axios.create({
  baseURL: 'http://localhost:8080',
  headers: { Authorization: `Bearer ${localStorage.getItem('wb_admin_token')}` },
});

const STATUS_CFG = {
  COMPLETED: { color: '#22c55e', bg: 'rgba(34,197,94,0.12)',  label: 'Completed' },
  STARTED:   { color: '#06b6d4', bg: 'rgba(6,182,212,0.12)',  label: 'Started'   },
  PAUSED:    { color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', label: 'Paused'    },
};

const formatDuration = (seconds) => {
  if (!seconds && seconds !== 0) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

const distanceColor = (km) => {
  if (!km) return 'var(--text-primary)';
  if (km >= 5) return '#22c55e';
  if (km >= 2) return '#f59e0b';
  return 'var(--text-primary)';
};

const WalkSessions = () => {
  const { addLog } = useActivity();
  const [data, setData]             = useState([]);
  const [stats, setStats]           = useState(null);
  const [total, setTotal]           = useState(0);
  const [loading, setLoading]       = useState(true);
  const [page, setPage]             = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [userIdInput, setUserIdInput]   = useState('');
  const [userIdFilter, setUserIdFilter] = useState('');
  const SIZE = 20;

  const loadStats = useCallback(async () => {
    try {
      const res = await api().get('/api/admin/walk-sessions/stats');
      setStats(res.data?.data);
    } catch { /* silent */ }
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, size: SIZE };
      if (statusFilter)  params.status = statusFilter;
      if (userIdFilter)  params.userId = userIdFilter;
      const res = await api().get('/api/admin/walk-sessions', { params });
      setData(res.data?.data || []);
      setTotal(res.data?.total || 0);
    } catch { toast.error('Failed to load walk sessions'); }
    finally { setLoading(false); }
  }, [page, statusFilter, userIdFilter]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadData(); }, [loadData]);

  const handleSearch = () => {
    setUserIdFilter(userIdInput);
    setPage(0);
  };

  const totalPages = Math.ceil(total / SIZE);

  return (
    <div className="animate-in">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '24px' }}>
        <div>
          <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', marginBottom: '4px' }}>
            Activity / <span style={{ color: 'var(--text-secondary)' }}>Walk Sessions</span>
          </div>
          <h1 style={{ margin: 0, fontSize: '1.6rem', fontWeight: 800 }}>🚶 Walk Sessions</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
            Track all user walking activity and session data
          </p>
        </div>
      </div>

      {/* Stat Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: '14px', marginBottom: '24px' }}>
        {[
          { label: 'Total Sessions',   value: stats?.totalSessions    ?? '—', color: 'var(--accent-primary)', icon: '📊' },
          { label: 'Completed',        value: stats?.completedSessions ?? '—', color: '#22c55e',               icon: '✅' },
          { label: 'Total KM',         value: stats?.totalKm != null ? `${Number(stats.totalKm).toFixed(1)} km` : '—', color: '#06b6d4', icon: '🗺️' },
          { label: 'Total Calories',   value: stats?.totalCalories    ?? '—', color: '#f59e0b',               icon: '🔥' },
          { label: 'Battle-Linked',    value: stats?.battleLinked      ?? '—', color: '#8b5cf6',               icon: '⚔️' },
        ].map((s, i) => (
          <div key={i} style={cardS}>
            <div style={{ fontSize: '1.4rem' }}>{s.icon}</div>
            <div style={{ fontSize: '1.4rem', fontWeight: 800, color: s.color, marginTop: '4px' }}>{s.value}</div>
            <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <select
          style={selS}
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPage(0); }}
        >
          <option value="">All Statuses</option>
          <option value="STARTED">STARTED</option>
          <option value="COMPLETED">COMPLETED</option>
          <option value="PAUSED">PAUSED</option>
        </select>
        <div style={{ display: 'flex', gap: '6px', flex: 1 }}>
          <input
            style={{ ...selS, flex: 1 }}
            placeholder="Filter by User ID…"
            value={userIdInput}
            onChange={e => setUserIdInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
          />
          <button style={primaryBtnS} onClick={handleSearch}>🔍 Search</button>
          {userIdFilter && (
            <button style={cancelBtnS} onClick={() => { setUserIdFilter(''); setUserIdInput(''); setPage(0); }}>✕ Clear</button>
          )}
        </div>
        <span style={{ padding: '9px 14px', background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', borderRadius: '8px', fontSize: '0.82rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
          {total.toLocaleString()} records
        </span>
      </div>

      {/* Table */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '12px', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-color)' }}>
                {['#', 'User', 'Status', 'Distance', 'Calories', 'Duration', 'Battle', 'Start Time', 'End Time'].map(h => (
                  <th key={h} style={thS}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: '50px', color: 'var(--text-muted)' }}>Loading…</td></tr>
              ) : data.length === 0 ? (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: '50px', color: 'var(--text-muted)' }}>No sessions found</td></tr>
              ) : data.map((row, i) => {
                const cfg = STATUS_CFG[row.status] || STATUS_CFG.STARTED;
                const km = row.totalDistance != null ? Number(row.totalDistance) : null;
                return (
                  <tr key={row.id}
                    style={{ borderBottom: '1px solid var(--border-color)', transition: 'background 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ ...tdS, color: 'var(--text-muted)', fontSize: '0.78rem' }}>{page * SIZE + i + 1}</td>
                    <td style={tdS}>
                      <div style={{ fontSize: '0.84rem', fontWeight: 600 }}>{row.userName || '—'}</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>ID: {row.userId}</div>
                    </td>
                    <td style={tdS}>
                      <span style={{ fontSize: '0.76rem', fontWeight: 700, padding: '3px 10px', borderRadius: '20px',
                        background: cfg.bg, color: cfg.color }}>
                        {cfg.label}
                      </span>
                    </td>
                    <td style={tdS}>
                      <span style={{ fontWeight: 700, fontSize: '0.9rem', color: distanceColor(km) }}>
                        🏃 {km != null ? km.toFixed(2) + ' km' : '—'}
                      </span>
                    </td>
                    <td style={tdS}>
                      <span style={{ fontSize: '0.88rem', color: '#f97316', fontWeight: 600 }}>
                        🔥 {row.caloriesBurned != null ? row.caloriesBurned.toLocaleString() : '—'}
                      </span>
                    </td>
                    <td style={{ ...tdS, fontSize: '0.84rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                      {formatDuration(row.duration)}
                    </td>
                    <td style={tdS}>
                      {row.battleId
                        ? <span style={{ fontSize: '0.76rem', fontWeight: 700, padding: '3px 10px', borderRadius: '20px', background: 'rgba(139,92,246,0.12)', color: '#8b5cf6' }}>⚔️ Battle</span>
                        : <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Solo</span>
                      }
                    </td>
                    <td style={{ ...tdS, fontSize: '0.78rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                      {row.startTime ? new Date(row.startTime).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                    </td>
                    <td style={{ ...tdS, fontSize: '0.78rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                      {row.endTime ? new Date(row.endTime).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderTop: '1px solid var(--border-color)' }}>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
              {page * SIZE + 1}–{Math.min((page + 1) * SIZE, total)} of {total}
            </span>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button disabled={page === 0} onClick={() => setPage(p => p - 1)}
                style={{ ...paginBtnS, opacity: page === 0 ? 0.4 : 1 }}>‹</button>
              <span style={{ padding: '5px 12px', background: 'var(--accent-primary)', color: 'white', borderRadius: '6px', fontSize: '0.82rem', fontWeight: 600 }}>
                {page + 1} / {totalPages}
              </span>
              <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}
                style={{ ...paginBtnS, opacity: page >= totalPages - 1 ? 0.4 : 1 }}>›</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const cardS       = { background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '18px', textAlign: 'center' };
const thS         = { padding: '10px 14px', textAlign: 'left', fontSize: '0.73rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', whiteSpace: 'nowrap' };
const tdS         = { padding: '10px 14px' };
const selS        = { padding: '9px 14px', background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '0.88rem', outline: 'none' };
const primaryBtnS = { padding: '9px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600, background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: 'white' };
const cancelBtnS  = { padding: '9px 14px', borderRadius: '8px', border: '1px solid var(--border-color)', cursor: 'pointer', fontSize: '0.88rem', background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' };
const paginBtnS   = { padding: '5px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', cursor: 'pointer', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' };

export default WalkSessions;
